import { useEffect } from 'react';

/**
 * Set the browser tab title for the current page.
 *
 * WHY THIS EXISTS, AND WHY IT IS DELIBERATELY TITLE-ONLY
 * -----------------------------------------------------
 * This replaces `react-helmet-async`, which was bundled and mounted but produced
 * NOTHING in the browser (React 19 incompatibility). That was proven, not inferred:
 * a MutationObserver on <head> recorded ZERO mutations across an in-app navigation
 * in which the page demonstrably re-rendered. The visible cost was that after any
 * in-app search the tab — and therefore every bookmark, history entry and share-sheet
 * title — still read "Martial Arts Network".
 *
 * The obvious replacement is React 19's native document metadata (rendering <title>,
 * <meta> and <link> from a component). That was measured before being adopted, and
 * REJECTED. React does not replace tags already present in the head — it ADDS to them.
 * With the SSG's tags already in the shell head (`injectHeadTags` in lib/ssgHead.ts,
 * outside React's tree) the measured result on a prerendered page was:
 *
 *   <title>            two elements — React's first, so document.title is correct
 *   <link canonical>   TWO, with DIFFERENT hrefs
 *   <meta robots>      TWO, with CONTRADICTING values
 *
 * A duplicated canonical pointing somewhere else, on ~2,300 pages, is far worse than
 * the stale tab title it would have fixed — Google discards conflicting canonicals.
 *
 * So the runtime owns exactly one thing: `document.title`, set imperatively, creating
 * no elements and duplicating nothing. Description, canonical and robots stay solely
 * with the SSG, which is correct there and is the copy crawlers actually read — they
 * fetch each URL directly and never navigate in-app, so there is no crawler cost.
 *
 * A consequence worth knowing: because the browser never writes a `robots` value,
 * the thin-suburb noindex trap (411 pages the platform deliberately keeps out of
 * Google) cannot fire from the client at all. That is now true by construction
 * rather than by a guard that has to stay correct.
 */
export function useDocumentTitle(title: string | undefined | null): void {
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!title) return;
        if (document.title === title) return;
        document.title = title;
    }, [title]);
}
